import {Loaded} from 'Core/models/Async';
import {eqValidatableEmail, ValidatableEmail, ValidEmail} from 'Core/models/Email';
import {eqStringUnion, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {eqValidatableNonEmptyString, ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {EmptyPhoneNumber, eqValidatablePhone, ValidatablePhone, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {RoleId} from 'Core/models/Role';
import {isValid} from 'Core/models/Validatable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {prop} from 'Shared/utilities/prop';
import {left, right} from 'fp-ts/lib/Either';
import {Eq, getStructEq} from 'fp-ts/lib/Eq';
import {nanoid} from 'nanoid';
import {ExistingTaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';
import {
  AgencyAssociation,
  AgencySupportVisibilityPermission,
  eqArrayOfAgencyAssociations,
  ValidAgencyAssociation,
} from 'Users/models/AgencyAssociation';
import {ValidAgentNumber} from 'Users/models/AgentNumber';

export type AgentMappingValue = {
  email: ValidatableEmail;
  firstName: StringUnion;
  lastName: StringUnion;
  userName: ValidatableNonEmptyString;
  phoneNumber: ValidatablePhone;
  agencyAssociations: AgencyAssociation[];
  isDisabled: boolean;
};

type AgencyAssociationsPayload = {
  agentNumbers: [
    {
      value: string;
      isNewYork: boolean;
      recruiterCode: string;
    }
  ];
  roles: Array<{
    name: string;
    id: RoleId;
  }>;
  maskedIdentifier: string;
  isNewYork: boolean;
  encryptedIdentifier: string;
  recruiterCodes?: string[];
  scopes: string[];
};

export type AgentMappingPayload = {
  email: string;
  firstName: string;
  agencyAssociations: AgencyAssociationsPayload[];
  lastName: string;
  taxIdNumber?: string;
  roles: string[];
  userName: string;
  mobilePhone?: string;
  isDisabled: boolean;
};

export const eqAgentMapping: Eq<AgentMappingValue> = getStructEq({
  agencyAssociations: eqArrayOfAgencyAssociations,
  email: eqValidatableEmail,
  firstName: eqStringUnion,
  lastName: eqStringUnion,
  phoneNumber: eqValidatablePhone,
  userName: eqValidatableNonEmptyString,
});

export const toPermission = (role: RoleId) => {
  return role === RoleId.AgencySupportWithAccounts
    ? AgencySupportVisibilityPermission.Account
    : role === RoleId.AgencySupportWithPolicies
    ? AgencySupportVisibilityPermission.Policies
    : role === RoleId.AgencySupportWithCommissionsAnd1099s
    ? AgencySupportVisibilityPermission.CommissionsAnd1099s
    : role === RoleId.AgencySupportWithPolicyServiceRequest
    ? AgencySupportVisibilityPermission.PolicyServiceRequest
    : role === RoleId.AgencySupportWithCorrespondence
    ? AgencySupportVisibilityPermission.Correspondence
    : role === RoleId.AgencySupportWithClaims
    ? AgencySupportVisibilityPermission.Claims
    : role === RoleId.AgencySupportWithUnderwritingRequirements
    ? AgencySupportVisibilityPermission.UnderwritingRequirements
    : '';
};

export const toAgentMappingValue = (agentPayload: AgentMappingPayload): AgentMappingValue => ({
  agencyAssociations: agentPayload.agencyAssociations.map(agencyAssociation =>
    ValidAgencyAssociation.create(
      nanoid(),
      agencyAssociation.roles.map(prop('id')).includes(RoleId.AgencyAdministrator),
      new ExistingTaxIdentificationNumber(agencyAssociation.maskedIdentifier, agencyAssociation.encryptedIdentifier),
      agencyAssociation.agentNumbers.map(numberMapping =>
        ValidAgentNumber.create(numberMapping.value, numberMapping.isNewYork)
      ) as ValidAgentNumber[],
      (agencyAssociation.roles
        .map(role => role.id)
        .map(toPermission)
        .filter(id => id !== '') as unknown) as AgencySupportVisibilityPermission[]
    )
  ),
  email: new Loaded(right(ValidEmail.create(agentPayload.email) as ValidEmail)),
  firstName: NonEmptyString.create(agentPayload.firstName),
  lastName: NonEmptyString.create(agentPayload.lastName),
  phoneNumber: agentPayload.mobilePhone
    ? new Loaded(right(ValidPhoneNumber.create(agentPayload.mobilePhone) as ValidPhoneNumber))
    : new Loaded(left(new EmptyPhoneNumber())),
  userName: new Loaded(right(NonEmptyString.create(agentPayload.userName) as NonEmptyString)),
  isDisabled: agentPayload.isDisabled,
});

export class UnvalidatedAgentMapping {
  public readonly key = 'UnvalidatedAgentMapping';

  constructor(public readonly value: AgentMappingValue) {}
}

export class InvalidAgentMapping {
  public readonly key = 'InvalidAgentMapping';

  constructor(public readonly value: AgentMappingValue) {}
}

export class ValidAgentMapping {
  public static create(agentMapping: AgentMappingValue) {
    const agencyAssociations = agentMapping.agencyAssociations.map(association =>
      ValidAgencyAssociation.create(
        association.id,
        association.isAdministrator,
        association.taxIdNumber,
        association.agentNumbers,
        association.visibilityPermissions
      )
    );

    return isValid(agentMapping.email) &&
      isValid(agentMapping.userName) &&
      agentMapping.firstName instanceof NonEmptyString &&
      agentMapping.lastName instanceof NonEmptyString &&
      isValid(agentMapping.phoneNumber) &&
      agencyAssociations.every(association => association instanceof ValidAgencyAssociation) &&
      agentMapping.agencyAssociations.length > 0
      ? new ValidAgentMapping({...agentMapping, agencyAssociations})
      : new InvalidAgentMapping(agentMapping);
  }

  public readonly key = 'ValidAgentMapping';

  private constructor(public readonly value: AgentMappingValue) {}
}

export class SavedAgentMapping {
  public readonly key = 'SavedAgentMapping';

  public constructor(public readonly value: AgentMappingValue, public readonly id: string) {}
}

export class ErrorSavingAgentMapping {
  public readonly key = 'ErrorSavingAgentMapping';

  public constructor(public readonly value: AgentMappingValue, public readonly error: string) {}
}

export type AgentMapping =
  | InvalidAgentMapping
  | ValidAgentMapping
  | UnvalidatedAgentMapping
  | SavedAgentMapping
  | ErrorSavingAgentMapping;

type AgentMappingOptions<TReturn> = ToMatchOptions<AgentMapping, 'key', TReturn>;

export const matchAgentMapping = <TReturn>(agentNumber: AgentMapping, options: AgentMappingOptions<TReturn>) => {
  switch (agentNumber.key) {
    case 'InvalidAgentMapping':
      return options.InvalidAgentMapping(agentNumber);
    case 'UnvalidatedAgentMapping':
      return options.UnvalidatedAgentMapping(agentNumber);
    case 'ValidAgentMapping':
      return options.ValidAgentMapping(agentNumber);
    case 'ErrorSavingAgentMapping':
      return options.ErrorSavingAgentMapping(agentNumber);
    case 'SavedAgentMapping':
      return options.SavedAgentMapping(agentNumber);
  }
};
